import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import { hasPermission } from "@saleor/auth/misc";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import { PermissionEnum } from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import PayoutPermissions from "../../PayoutPermissions";
import { PayoutStoreOrders_payoutStoreOrders_edges_node } from "../../types/PayoutStoreOrders";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      clickRow: {
        cursor: "pointer"
      },
      colCenter: {
        textAlign: "center"
      },
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1),
          textAlign: "center"
        }
      },
      colWide: {
        overflowing,
        width: "19em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        "& td": {
          fontSize: "15px"
        },
        "& th": {
          fontSize: "15px"
        },
        tableLayout: "fixed"
      }
    };
  },
  { name: "PayoutsDetailsList" }
);

interface PayoutsDetailsListProp extends ListProps {
  payouts: PayoutStoreOrders_payoutStoreOrders_edges_node[];
  payoutStatus: string;
  user: any;
  onSettlementEdit: (id: string) => void;
  onPayable: (id: string) => void;
}

const PayoutsDetailsList: React.FC<PayoutsDetailsListProp> = ({
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  onSettlementEdit,
  onPayable,
  payoutStatus,
  user,
  pageInfo,
  payouts,
  settings,
  disabled
}) => {
  const userPerm = user?.userPermissions?.map(p => p.code);
  const perm = [
    PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
    PermissionEnum.MANAGE_PAYOUT_ON_PROCESS
  ];
  const isOpenOrder =
    hasPermission(PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER, user) &&
    (payoutStatus === "Schedule for Payout" || payoutStatus === "Returned");

  const forPayment =
    hasPermission(PermissionEnum.MANAGE_PAYOUT_PAYMENT, user) &&
    payoutStatus === "Ready for payment";
  const hasPerm = userPerm.filter(e => perm.includes(e));
  const colNum = hasPerm?.length ? 26 : 4;
  const classes = useStyles({});
  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          {/* TREASURY 1 AND 2 */}
          <PayoutPermissions
            userPermissions={user.userPermissions}
            requiredPermissions={[
              PermissionEnum.MANAGE_PAYOUT_PAYABLES,
              PermissionEnum.MANAGE_PAYOUT_PAYMENT
            ]}
          >
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Bank Account Name" />
            </TableCellHeader>
          </PayoutPermissions>
          {/* SPECIALIST AND MANAGER */}
          <PayoutPermissions
            userPermissions={user.userPermissions}
            requiredPermissions={[
              PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
              PermissionEnum.MANAGE_PAYOUT_ON_PROCESS
            ]}
          >
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Created Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Completion Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Store Order Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Payment Method" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Gross Amount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Tenant Discount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="SMO Discount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Discount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Sales Amount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Delivery Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Gross Transaction" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Tenant Voucher" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="SMO Voucher" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Voucher" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Price Paid" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="VAT on Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Adjustment on Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Adjustment VAT on Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Total Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Withholding Tax on Payment Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Witholding Tax on Credit Card Transaction" />
            </TableCellHeader>
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Other Adjustment" />
            </TableCellHeader>
          </PayoutPermissions>
          {/* END SPECIALIST AND MANAGER */}
          {/* SPECIALIST,MANAGER, TS1 and TS2 */}
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Net Settlement" />
          </TableCellHeader>
          {/* END SPECIALIST,MANAGER, TS1 and TS2 */}
          {/* SPECIALIST AND MANAGER */}
          <PayoutPermissions
            userPermissions={user.userPermissions}
            requiredPermissions={[
              PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
              PermissionEnum.MANAGE_PAYOUT_ON_PROCESS
            ]}
          >
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Settlement Status" />
            </TableCellHeader>
          </PayoutPermissions>
          {/* END SPECIALIST AND MANAGER */}

          {/* SPECIALIST,MANAGER, TS1 and TS2 */}
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Settlement Date" />
          </TableCellHeader>

          <PayoutPermissions
            userPermissions={user.userPermissions}
            requiredPermissions={[PermissionEnum.MANAGE_PAYOUT_PAYMENT]}
          >
            <TableCellHeader className={classes.colMedium}>
              <FormattedMessage defaultMessage="Payable List" />
            </TableCellHeader>
          </PayoutPermissions>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {payouts &&
            renderCollection(
              payouts,
              payout => (
                <TableRow
                  className={classes.clickRow}
                  hover={!!payout}
                  key={payout ? payout.id : "skeleton"}
                  onClick={
                    !!payout && isOpenOrder
                      ? () => onSettlementEdit(payout.id)
                      : !!payout && forPayment
                      ? () => onPayable(payout.id)
                      : undefined
                  }
                >
                  {/* TREASURY 1 and 2 */}
                  <PayoutPermissions
                    userPermissions={user.userPermissions}
                    requiredPermissions={[
                      PermissionEnum.MANAGE_PAYOUT_PAYABLES,
                      PermissionEnum.MANAGE_PAYOUT_PAYMENT
                    ]}
                  >
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => payout.bank_account_name,
                        <Skeleton />
                      )}
                    </TableCell>
                  </PayoutPermissions>
                  {/* SPECIALIST AND MANAGER */}
                  <PayoutPermissions
                    userPermissions={user.userPermissions}
                    requiredPermissions={[
                      PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
                      PermissionEnum.MANAGE_PAYOUT_ON_PROCESS
                    ]}
                  >
                    <TableCell>
                      <Moment format="YYYY-MM-DD hh:mm:ss" tz="Asia/Manila">
                        {payout?.order_date ? payout.order_date : <Skeleton />}
                      </Moment>
                    </TableCell>
                    <TableCell>
                      <Moment format="YYYY-MM-DD hh:mm:ss" tz="Asia/Manila">
                        {payout?.completed_at ? (
                          payout.completed_at
                        ) : (
                          <Skeleton />
                        )}
                      </Moment>
                    </TableCell>

                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => payout.store_order_code,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => payout?.payment_method_type?.toUpperCase(),
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.total_retail_price,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.sale_discount_tenant,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.sale_discount_sm,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.sale_discount_total,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.total_selling_price,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.delivery_fee,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.total_gross_transaction,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.voucher_discount_tenant,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.voucher_discount_sm,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.voucher_discount_total,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.price_paid,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.payment_fee,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.payment_fee_vat,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.payment_fee_adjustment,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.payment_fee_adjustment_vat,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.total_payment_fee,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.payment_fee_witholding_tax,
                        <Skeleton />
                      )}
                    </TableCell>

                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.credit_card_witholding_tax,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colCenter}>
                      {maybe<React.ReactNode>(
                        () => payout.other_adjustment,
                        <Skeleton />
                      )}
                    </TableCell>
                  </PayoutPermissions>
                  {/* END SPECIALIST AND MANAGER */}

                  <TableCell className={classes.colCenter}>
                    {maybe<React.ReactNode>(
                      () => payout.net_settlement,
                      <Skeleton />
                    )}
                  </TableCell>

                  {/* SPECIALIST AND MANAGER */}
                  <PayoutPermissions
                    userPermissions={user.userPermissions}
                    requiredPermissions={[
                      PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
                      PermissionEnum.MANAGE_PAYOUT_ON_PROCESS
                    ]}
                  >
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => payout.settlement_status,
                        <Skeleton />
                      )}
                    </TableCell>
                  </PayoutPermissions>
                  <TableCell>
                    {payout?.settlement_date ? (
                      <Moment format="YYYY-MM-DD" tz="Asia/Manila">
                        {payout?.settlement_date}
                      </Moment>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>

                  <PayoutPermissions
                    userPermissions={user.userPermissions}
                    requiredPermissions={[PermissionEnum.MANAGE_PAYOUT_PAYMENT]}
                  >
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => payout.payable_status,
                        <Skeleton />
                      )}
                    </TableCell>
                  </PayoutPermissions>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No payouts found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default PayoutsDetailsList;
